import { useNavigate } from "react-router-dom";
import { ArrowRight, Briefcase, MapPin, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { careersCopy } from "@/lib/siteCopy";

const openRoles = [
  {
    title: "Full-Stack Engineer",
    titleHi: "फुल-स्टैक इंजीनियर",
    type: "Full-time · Remote",
    typeHi: "फुल-टाइम · रिमोट",
    focus: "React, Node.js, cloud-native delivery",
    focusHi: "React, Node.js, क्लाउड-नेटिव डिलीवरी",
  },
  {
    title: "AI / ML Engineer",
    titleHi: "AI / ML इंजीनियर",
    type: "Full-time · Remote",
    typeHi: "फुल-टाइम · रिमोट",
    focus: "Applied ML, FastAPI, production systems",
    focusHi: "एप्लाइड ML, FastAPI, प्रोडक्शन सिस्टम",
  },
  {
    title: "Product Designer",
    titleHi: "प्रोडक्ट डिज़ाइनर",
    type: "Contract · Remote",
    typeHi: "कॉन्ट्रैक्ट · रिमोट",
    focus: "Product UI, design systems, motion",
    focusHi: "प्रोडक्ट UI, डिज़ाइन सिस्टम, मोशन",
  },
];

export function CareersPage() {
  const navigate = useNavigate();
  const lang = useSiteLanguage();
  const t = careersCopy[lang];

  return (
    <>
      <SEO
        title="Careers at DevDotCom - Join the Engineering Team"
        description="Explore open roles at DevDotCom. Build products with an engineering-first team shipping web, AI, and cloud solutions."
        keywords="DevDotCom careers, software engineering jobs, remote developer jobs India, join DevDotCom"
      />

      <section className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,oklch(96%_0_0)_0%,transparent_55%)] dark:bg-[radial-gradient(ellipse_at_top,oklch(20%_0_0)_0%,transparent_55%)]" />

        <div className="relative mx-auto max-w-4xl px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="mb-4 inline-flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <Briefcase className="h-4 w-4" />
              {t.eyebrow}
            </p>
            <h1 className="text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
              {t.title}
            </h1>
            <p className="mt-5 text-lg text-muted-foreground md:text-xl">
              {t.subtitle}
            </p>
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4 shrink-0" />
              {t.location}
            </div>
          </div>

          <div className="mt-14 space-y-3">
            {openRoles.map((role) => {
              const title = lang === "hi" ? role.titleHi : role.title;
              const type = lang === "hi" ? role.typeHi : role.type;
              const focus = lang === "hi" ? role.focusHi : role.focus;
              return (
                <button
                  key={role.title}
                  type="button"
                  onClick={() =>
                    navigate("/contact", {
                      state: {
                        prefill: {
                          query: `Careers - ${role.title}`,
                          message: `Hi DevDotCom team,\n\nI'd like to apply for the ${role.title} role.\n\n`,
                        },
                      },
                    })
                  }
                  className="group flex w-full flex-col gap-2 rounded-2xl border border-border bg-background px-5 py-5 text-left transition-colors hover:border-foreground/20 hover:bg-muted/40 sm:flex-row sm:items-center sm:justify-between"
                >
                  <div>
                    <h2 className="text-lg font-semibold tracking-tight">
                      {title}
                    </h2>
                    <p className="mt-1 text-sm text-muted-foreground">{type}</p>
                    <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Sparkles className="h-3.5 w-3.5" />
                      {focus}
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-foreground">
                    {t.apply}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </button>
              );
            })}
          </div>

          <div className="mt-12 rounded-2xl border border-border bg-muted/30 px-6 py-8 text-center">
            <h2 className="text-xl font-semibold tracking-tight">
              {t.openTitle}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-muted-foreground">
              {t.openBody}
            </p>
            <Button
              size="lg"
              className="mt-6 rounded-xl px-8"
              onClick={() =>
                navigate("/contact", {
                  state: {
                    prefill: {
                      query: "Careers - Open application",
                      message:
                        "Hi DevDotCom team,\n\nI'd like to introduce myself for future openings.\n\n",
                    },
                  },
                })
              }
            >
              {t.getInTouch}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>
    </>
  );
}
